/**
 * The `[1]` marker in an answer.
 *
 * Orange, per the charte, which is the action colour — and a citation is an action: "show me where
 * that came from". `accentStrong` for the text rather than `accent`, because the button face is the
 * pale tint and `#ff7e20` on `#fff7ed` at 11px does not meet contrast.
 *
 * A button, not a span, because it opens the source. The single most important interaction in this
 * product is "show me where that came from", and it has to be reachable by keyboard.
 */

import { cx } from "./cx";

export interface CitationChipProps {
  index: number;
  onClick?: () => void;
  /** Accessible name: "source 1", localized by the caller. */
  label: string;
  active?: boolean;
  className?: string;
}

export function CitationChip({
  index,
  onClick,
  label,
  active,
  className,
}: CitationChipProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cx(
        // Margin on the start side only, not both.
        //
        // `mx-1` put a gap on the far side too, so "…7 LYD [1]." rendered as "…7 LYD [1] ." with the
        // full stop adrift. It reads as a typo in English and worse in Arabic, where the detached
        // stop lands at the left edge of the line and looks like it belongs to the next sentence.
        // Logical, so it mirrors: the space is always before the marker in reading order.
        "ms-1 inline-flex min-w-6 items-center justify-center rounded-sm border px-1 align-baseline",
        "text-label-sm font-semibold transition-colors",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent",
        active
          ? "border-accent bg-accent text-onPrimary"
          : "border-accentBorder bg-accentSubtle text-accentStrong hover:bg-accent hover:text-onPrimary",
        className,
      )}
    >
      {index}
    </button>
  );
}
